import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { login, register } from '../store/authSlice';
import authService from '../services/authService';
import { Marca } from '../components/Marca';
import SelectorIdioma from '../components/SelectorIdioma';
import PeuStrategos from '../components/PeuStrategos';

// LA PANTALLA D'ENTRADA.
//
// Una sola targeta amb dues pestanyes, i prou. La que hi havia era una pàgina de venda a dues
// columnes —«Collaborative Learning Platform», tres reclams que no existien i tres comptes de
// demostració que no han existit mai— i el client la volia pragmàtica.
//
// **`/register` ja no existeix.** Hi havia dues pantalles de registre que feien la mateixa feina
// i a la segona no hi enllaçava ningú; ara tot passa per aquí. La ruta hi redirigeix, perquè algú
// pot tenir l'adreça vella desada.
//
// Cinc vistes, no dues: entrar, crear un compte, i les tres que surten de coses que abans no
// existien —el compte que espera aprovació, demanar un codi, i posar-se la contrasenya amb ell—.
// Les tres últimes substitueixen les pestanyes en comptes d'afegir-n'hi una, perquè són camins
// de sortida d'un problema concret i no una opció permanent.

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { loading, error } = useSelector((state) => state.auth);

  const [vista, setVista] = useState('entrar');   // entrar · registre · pendent · oblidada · codi
  const [motiuBarrat, setMotiuBarrat] = useState(null);
  const [feina, setFeina] = useState(false);

  const [entrada, setEntrada] = useState({ email: '', password: '' });
  const [alta, setAlta] = useState({
    name: '', email: '', password: '', confirmPassword: ''
  });
  const [oblidada, setOblidada] = useState({ email: '' });
  const [codi, setCodi] = useState({ email: '', code: '', password: '', confirmPassword: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setMotiuBarrat(null);
    const result = await dispatch(login(entrada));
    if (login.fulfilled.match(result)) {
      toast.success(t('auth.signedIn'));
      navigate('/dashboard');
    } else {
      // Contrasenya equivocada i compte que espera aprovació són coses molt diferents i
      // s'arreglen de manera molt diferent. El servidor les distingeix amb `reason`; ensenyar
      // el mateix toast vermell per a totes dues faria que algú provés vint cops una
      // contrasenya que ja té bé.
      const dades = result.payload || {};
      const motiu = typeof dades === 'object' ? dades.reason : null;
      if (motiu) setMotiuBarrat(motiu);
      else toast.error((typeof dades === 'object' ? dades.error : dades) || t('auth.signInFailed'));
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!alta.name || !alta.email || !alta.password) return toast.error(t('auth.allFieldsRequired'));
    // Vuit, no sis: és el mínim que exigeix el backend. Amb sis, el formulari donava el vistiplau
    // i després arribava un 400 que aquí no es veia enlloc.
    if (alta.password.length < 8) return toast.error(t('auth.passwordTooShort'));
    if (alta.password !== alta.confirmPassword) return toast.error(t('auth.passwordsDoNotMatch'));
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(alta.email)) return toast.error(t('auth.invalidEmail'));

    const result = await dispatch(register({
      email: alta.email,
      password: alta.password,
      name: alta.name.trim(),
    }));

    if (register.fulfilled.match(result)) setVista('pendent');
    else toast.error(result.payload || t('auth.registerFailed'));
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setFeina(true);
    try {
      await authService.forgot(oblidada.email);
      setCodi((c) => ({ ...c, email: oblidada.email }));
      setVista('codi');
      toast.success(t('forgot.sent'));
    } catch (err) {
      toast.error(err.response?.data?.error || t('forgot.failed'));
    } finally { setFeina(false); }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (codi.password.length < 8) return toast.error(t('auth.passwordTooShort'));
    if (codi.password !== codi.confirmPassword) return toast.error(t('auth.passwordsDoNotMatch'));
    setFeina(true);
    try {
      await authService.reset(codi.email, codi.code, codi.password);
      toast.success(t('reset.done'));
      setEntrada({ email: codi.email, password: '' });
      setCodi({ email: '', code: '', password: '', confirmPassword: '' });
      setVista('entrar');
    } catch (err) {
      toast.error(err.response?.data?.error || t('reset.failed'));
    } finally { setFeina(false); }
  };

  const camp = (etiqueta, props) => (
    <div>
      <label htmlFor={props.id} className="block text-sm font-medium text-gray-700 mb-1">
        {etiqueta}
      </label>
      <input
        className="w-full px-3.5 py-2.5 border border-gray-300 rounded-md text-[15px]
                   focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
        {...props} />
    </div>
  );

  const primari = (text, ocupat) => (
    <button type="submit" disabled={ocupat}
      className="w-full py-3 bg-primary-700 text-white rounded-md font-semibold
                 hover:bg-primary-800 disabled:opacity-60 transition-colors">
      {text}
    </button>
  );

  const pestanya = (clau, etiqueta) => (
    <button type="button" onClick={() => { setVista(clau); setMotiuBarrat(null); }}
      className={`py-3.5 text-center text-sm font-semibold transition-colors ${
        vista === clau
          ? 'text-primary-700 border-b-2 border-primary-700 -mb-px'
          : 'text-gray-500 hover:text-gray-700'}`}>
      {etiqueta}
    </button>
  );

  const cos = () => {
    if (vista === 'pendent') return (
      <div className="p-7">
        <Marca mida={21} className="mb-6" />
        <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('pending.title')}</h2>
        <p className="text-sm text-gray-600 mb-3">{t('pending.body')}</p>
        <p className="text-sm text-gray-600 mb-6">{t('pending.noEmail')}</p>
        <button onClick={() => setVista('entrar')}
          className="w-full py-3 bg-primary-700 text-white rounded-md font-semibold hover:bg-primary-800">
          {t('pending.backToSignIn')}
        </button>
      </div>
    );

    if (vista === 'oblidada') return (
      <form onSubmit={handleForgot} className="p-7 space-y-5">
        <Marca mida={21} />
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{t('forgot.title')}</h2>
          <p className="text-sm text-gray-600 mt-1">{t('forgot.body')}</p>
        </div>
        {camp(t('auth.email'), { id: 'f-email', type: 'email', required: true, autoFocus: true,
          value: oblidada.email, onChange: (e) => setOblidada({ email: e.target.value }) })}
        {primari(feina ? t('forgot.sending') : t('forgot.submit'), feina)}
        <div className="flex justify-between text-sm">
          <button type="button" onClick={() => setVista('entrar')}
            className="text-gray-500 hover:text-gray-700">{t('auth.back')}</button>
          <button type="button" onClick={() => setVista('codi')}
            className="text-primary-700 font-medium hover:text-primary-800">{t('auth.haveACode')}</button>
        </div>
      </form>
    );

    if (vista === 'codi') return (
      <form onSubmit={handleReset} className="p-7 space-y-5">
        <Marca mida={21} />
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{t('reset.title')}</h2>
          <p className="text-sm text-gray-600 mt-1">{t('reset.body')}</p>
        </div>
        {camp(t('auth.email'), { id: 'r-email', type: 'email', required: true,
          value: codi.email, onChange: (e) => setCodi({ ...codi, email: e.target.value }) })}
        {camp(t('reset.code'), { id: 'r-code', type: 'text', required: true, autoFocus: true,
          spellCheck: 'false', autoComplete: 'off', placeholder: 'XXXX-XXXX',
          value: codi.code, onChange: (e) => setCodi({ ...codi, code: e.target.value }) })}
        {camp(t('reset.newPassword'), { id: 'r-pw', type: 'password', required: true,
          value: codi.password, onChange: (e) => setCodi({ ...codi, password: e.target.value }) })}
        {camp(t('auth.confirmPassword'), { id: 'r-pw2', type: 'password', required: true,
          value: codi.confirmPassword,
          onChange: (e) => setCodi({ ...codi, confirmPassword: e.target.value }) })}
        {primari(feina ? t('reset.changing') : t('reset.submit'), feina)}
        <button type="button" onClick={() => setVista('entrar')}
          className="w-full text-sm text-gray-500 hover:text-gray-700">{t('auth.back')}</button>
      </form>
    );

    if (vista === 'registre') return (
      <form onSubmit={handleRegister} className="p-7 space-y-4">
        <Marca mida={21} tagline />
        {camp(t('auth.name'), { id: 'n-name', type: 'text', required: true, autoComplete: 'name',
          value: alta.name, onChange: (e) => setAlta({ ...alta, name: e.target.value }) })}
        {camp(t('auth.email'), { id: 'n-email', type: 'email', required: true, autoComplete: 'email',
          value: alta.email, onChange: (e) => setAlta({ ...alta, email: e.target.value }) })}
        {camp(t('auth.password'), { id: 'n-pw', type: 'password', required: true, minLength: 8,
          autoComplete: 'new-password',
          value: alta.password, onChange: (e) => setAlta({ ...alta, password: e.target.value }) })}
        {camp(t('auth.confirmPassword'), { id: 'n-pw2', type: 'password', required: true,
          autoComplete: 'new-password', value: alta.confirmPassword,
          onChange: (e) => setAlta({ ...alta, confirmPassword: e.target.value }) })}

        {/* AQUÍ NO ES TRIA CAP ROL (27/08/2026). Hi havia un desplegable «alumne o docent»
            que quedava desat com a petició, i era soroll: qui concedeix el rol és qui aprova el
            compte, i tenir la petició al davant convida a fer-ne cas. Tothom entra com a
            modelador i l'admin puja qui hagi de pujar. */}

        {primari(loading ? t('auth.creating') : t('auth.createAccount'), loading)}
      </form>
    );

    return (
      <form onSubmit={handleLogin} className="p-7 space-y-4">
        <Marca mida={21} tagline />
        {camp(t('auth.email'), { id: 'e-email', type: 'email', required: true, autoFocus: true,
          autoComplete: 'username',
          value: entrada.email, onChange: (e) => setEntrada({ ...entrada, email: e.target.value }) })}
        {camp(t('auth.password'), { id: 'e-pw', type: 'password', required: true,
          autoComplete: 'current-password',
          value: entrada.password,
          onChange: (e) => setEntrada({ ...entrada, password: e.target.value }) })}
        {primari(loading ? t('auth.signingIn') : t('auth.signIn'), loading)}

        {motiuBarrat && (
          <div className="p-3 bg-warn-50 border border-warn-200 text-warn-900 rounded-md text-sm">
            {motiuBarrat === 'pending' && t('auth.blockedPending')}
            {motiuBarrat === 'rejected' && t('auth.blockedRejected')}
            {motiuBarrat === 'inactive' && t('auth.blockedInactive')}
          </div>
        )}

        {/* No hi diu «he oblidat la contrasenya» perquè no hi ha cap manera de recuperar-la
            sol: no s'envia cap correu des d'aquest servidor. El que hi ha és demanar un codi
            a una persona, i el text ho ha de dir. */}
        <div className="text-center text-sm pt-1">
          <button type="button" onClick={() => setVista('oblidada')}
            className="text-gray-500 hover:text-gray-700">{t('auth.cantSignIn')}</button>
          <span className="text-gray-500 mx-2">·</span>
          <button type="button" onClick={() => setVista('codi')}
            className="text-gray-500 hover:text-gray-700">{t('auth.haveACode')}</button>
        </div>
      </form>
    );
  };

  const ambPestanyes = vista === 'entrar' || vista === 'registre';

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <div className="flex-1 flex items-center justify-center p-4">
      <div className="w-full max-w-[400px]">
        {/* L'idioma es tria ABANS d'entrar. Fins al 27/08/2026 el selector només era dins de
            l'aplicació, o sigui que la primera pantalla que veu ningú —i l'única que veu qui
            encara no té compte— es quedava en anglès sense cap manera de canviar-ho. */}
        <div className="flex justify-end mb-3">
          <SelectorIdioma />
        </div>
        <div className="bg-white border border-gray-200 rounded-md shadow-sm overflow-hidden">
          {ambPestanyes && (
            <div className="grid grid-cols-2 border-b border-gray-200">
              {pestanya('entrar', t('auth.tabSignIn'))}
              {pestanya('registre', t('auth.tabRegister'))}
            </div>
          )}
          {ambPestanyes && error && (
            <div className="mx-7 mt-6 -mb-1 p-3 bg-danger-50 border border-danger-200 text-danger-800 rounded-md text-sm">
              {typeof error === 'string' && error.startsWith('errors.') ? t(error) : error}
            </div>
          )}
          {cos()}
        </div>
        </div>
      </div>

      <PeuStrategos />
    </div>
  );
};

export default Login;
